'use client'

import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useParams, useRouter } from 'next/navigation'
import { useTranslations } from 'next-intl'

import {
  AlertBox,
  AlertBoxType,
  Button,
  Heading,
  Input,
  InputHelperText,
  InputLabel,
  Paragraph,
} from '@graftcode/design-system'
import { zodResolver } from '@hookform/resolvers/zod'

import { AppRoute } from '@common/enums'

import { switchWorkspace } from '@modules/projects/utils/switchWorkspace'
import { useWorkspacesStore } from '@modules/workspaces/stores/workspaces.store'
import { createWorkspace, getWorkspacesByUserId } from '@modules/workspaces/stores/workspaces.thunks'
import { WORKSPACES_TEST_IDS } from '@modules/workspaces/workspaces.const'
import { useToastStore } from '@stores/toast/toast.store'

import { type CreateWorkspaceFormData, createWorkspaceSchema } from './Create.schema'

import styles from './Create.module.scss'

type Props = {
  onWorkspaceCreated?: (workspaceId: string) => void
}

export function CreateWorkspaceForm({ onWorkspaceCreated }: Props) {
  const router = useRouter()
  const { locale } = useParams()

  // Hooks
  const t = useTranslations('workspaces')
  const showToast = useToastStore((s) => s.showToast)
  const setIsCreatingWorkspace = useWorkspacesStore((s) => s.setIsCreatingWorkspace)

  // Form
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting, isValid, isDirty },
    setError,
  } = useForm<CreateWorkspaceFormData>({
    resolver: zodResolver(createWorkspaceSchema(t)),
    defaultValues: {
      name: '',
    },
  })

  useEffect(() => {
    setIsCreatingWorkspace(isSubmitting)
  }, [isSubmitting, setIsCreatingWorkspace])

  useEffect(() => {
    if (!isSubmitting) return
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault()
    }
    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [isSubmitting])

  // Handlers
  const handleCancel = () => {
    router.back()
  }

  const onSubmit = async (data: CreateWorkspaceFormData) => {
    try {
      const workspaceId = await createWorkspace(data.name)

      if (!workspaceId) {
        setError('root', {
          type: 'manual',
          message: t('create.errors.createFailed'),
        })
        showToast({
          variant: 'error',
          title: t('create.errors.alertTitle'),
          description: t('create.errors.createFailed'),
        })
        return
      }

      await getWorkspacesByUserId({ force: true })

      const newWorkspace = useWorkspacesStore.getState().workspaces.find((w) => w.workspaceId === workspaceId)

      if (!newWorkspace) {
        setError('root', {
          type: 'manual',
          message: t('create.errors.unexpectedError'),
        })
        showToast({
          variant: 'error',
          title: t('create.errors.alertTitle'),
          description: t('create.errors.unexpectedError'),
        })
        return
      }

      await switchWorkspace(newWorkspace)

      showToast({
        variant: 'success',
        title: t('create.toast.successTitle', { name: data.name }),
        description: t('create.toast.successDescription'),
        primaryAction: {
          label: t('create.toast.openSettings'),
          variant: 'primary',
          onClick: () => router.push(`/${locale}/${AppRoute.SETTINGS_WORKSPACE}`),
        },
        secondaryAction: {
          label: t('create.toast.close'),
          variant: 'ghost',
        },
      })

      onWorkspaceCreated?.(workspaceId)
    } catch (error) {
      console.error('Failed to create workspace:', error)
      setError('root', {
        type: 'manual',
        message: t('create.errors.unexpectedError'),
      })
      showToast({
        variant: 'error',
        title: t('create.errors.alertTitle'),
        description: t('create.errors.unexpectedError'),
      })
    }
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className={styles.form}>
      <div className={styles.formContent}>
        <div className={styles.formHeader}>
          <Heading type="h2" className={styles.header}>
            {t('create.title')}
          </Heading>
          <Paragraph className={styles.description}>{t('create.description')}</Paragraph>
        </div>

        <div className={styles.formFields}>
          <div className={styles.formField}>
            <InputLabel htmlFor="name">{t('create.nameLabel')}</InputLabel>
            <Input
              id="name"
              size="md"
              variant={errors.name ? 'error' : 'default'}
              placeholder={t('create.namePlaceholder')}
              disabled={isSubmitting}
              data-testid={WORKSPACES_TEST_IDS.createNameInput}
              {...register('name')}
            />
            {errors.name?.message && <InputHelperText variant="error">{errors.name.message}</InputHelperText>}
            {!errors.name?.message && <InputHelperText>{t('create.nameHelperText')}</InputHelperText>}
          </div>

          <div className={styles.formError}>
            {errors.root?.message && (
              <AlertBox
                size="sm"
                className={styles.alertBox}
                title={t('create.errors.alertTitle')}
                description={errors.root.message}
                type={AlertBoxType.Critical}
                showCloseButton={false}
              />
            )}
          </div>
        </div>
      </div>

      <div className={styles.formActions}>
        <Button type="button" variant="outlined" size="l" onClick={handleCancel} disabled={isSubmitting}>
          {t('create.cancelButton')}
        </Button>

        <Button
          type="submit"
          variant="primary"
          size="l"
          disabled={(!isDirty && !isValid) || isSubmitting}
          data-testid={WORKSPACES_TEST_IDS.createSubmit}
        >
          {isSubmitting ? t('create.submitting') : t('create.submitButton')}
        </Button>
      </div>
    </form>
  )
}
